import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

// [value, i18n label key] — labels are translated at render time.
const QUESTION_TYPES = [
  ["checkbox",  "checkbox"],
  ["pass_fail", "pass_fail"],
  ["text",      "text_input"],
  ["number",    "number"],
  ["dropdown",  "dropdown"]
]

export default class extends Controller {
  static targets = ["list", "input"]

  connect() {
    this.items = this.#parseInput()
    this.#renderAll()
    this._boundRefresh = this.#refresh.bind(this)
    this.inputTarget.addEventListener("json-checklist:refresh", this._boundRefresh)
  }

  disconnect() {
    this.inputTarget.removeEventListener("json-checklist:refresh", this._boundRefresh)
  }

  handleTemplateChange(event) {
    const opt = event.target.options[event.target.selectedIndex]
    const data = opt.dataset.items
    if (data && data !== "[]") {
      try {
        const raw = JSON.parse(data)
        this.items = raw.map(item => ({
          question_text: item.question_text || "",
          question_type: item.question_type || "checkbox",
          required: item.required !== false,
          options: item.options || null
        }))
      } catch(e) {
        this.items = []
      }
    } else {
      this.items = []
    }
    this.#renderAll()
    this.#saveToInput()
  }

  addItem() {
    this.items.push({ question_text: "", question_type: "checkbox", required: true })
    this.#renderAll()
    const inputs = this.listTarget.querySelectorAll("input[type='text']")
    if (inputs.length) inputs[inputs.length - 1].focus()
    this.#saveToInput()
  }

  updateText(event) {
    const index = parseInt(event.target.dataset.index)
    this.items[index].question_text = event.target.value
    this.#saveToInput()
  }

  updateType(event) {
    const index = parseInt(event.target.dataset.index)
    this.items[index].question_type = event.target.value
    this.#saveToInput()
  }

  updateRequired(event) {
    const index = parseInt(event.target.dataset.index)
    this.items[index].required = event.target.checked
    this.#saveToInput()
  }

  removeItem(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.items.splice(index, 1)
    this.#renderAll()
    this.#saveToInput()
  }

  // -- private ---------------------------------------------------------------

  #refresh() {
    this.items = this.#parseInput()
    this.#renderAll()
  }

  #parseInput() {
    try {
      const raw = JSON.parse(this.inputTarget.value || "[]")
      return (Array.isArray(raw) ? raw : []).map(item =>
        typeof item === "string"
          ? { question_text: item, question_type: "checkbox", required: true }
          : item
      )
    } catch(e) {
      return []
    }
  }

  #saveToInput() {
    this.inputTarget.value = JSON.stringify(this.items)
  }

  #renderAll() {
    this.listTarget.innerHTML = ""
    if (this.items.length === 0) {
      const empty = document.createElement("p")
      empty.className = "text-gray-500 text-sm text-center py-4"
      empty.textContent = t('javascript.controllers.json_checklist_editor_controller.no_items_yet_add_items_below_or_load_from_a_templa')
      this.listTarget.appendChild(empty)
      return
    }
    this.items.forEach((item, index) => this.#renderRow(item, index))
  }

  #renderRow(item, index) {
    const opts = QUESTION_TYPES.map(([val, labelKey]) =>
      `<option value="${val}"${item.question_type === val ? " selected" : ""}>${t(`javascript.controllers.json_checklist_editor_controller.question_types.${labelKey}`)}</option>`
    ).join("")

    const row = document.createElement("div")
    row.className = "flex items-center gap-2 p-3 bg-white rounded-lg border border-gray-300"
    row.innerHTML = `
      <input type="text"
             value="${this.#esc(item.question_text || "")}"
             placeholder="${this.#esc(t("javascript.controllers.json_checklist_editor_controller.question_text_placeholder"))}"
             class="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm text-gray-900 bg-gray-50 focus:border-primary focus:ring-1 focus:ring-primary min-w-0"
             data-action="input->json-checklist-editor#updateText"
             data-index="${index}">
      <select class="px-2 py-1.5 border border-gray-300 rounded text-sm text-gray-900 bg-gray-50 focus:border-primary shrink-0"
              data-action="change->json-checklist-editor#updateType"
              data-index="${index}">
        ${opts}
      </select>
      <label class="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap cursor-pointer shrink-0">
        <input type="checkbox" ${item.required !== false ? "checked" : ""}
               class="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
               data-action="change->json-checklist-editor#updateRequired"
               data-index="${index}">
        ${t("javascript.controllers.json_checklist_editor_controller.required_short")}
      </label>
      <button type="button"
              class="text-red-400 hover:text-red-600 transition-colors shrink-0"
              data-action="click->json-checklist-editor#removeItem"
              data-index="${index}">
        <span class="material-symbols-outlined !text-xl">delete</span>
      </button>
    `
    this.listTarget.appendChild(row)
  }

  #esc(str) {
    return String(str)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
